import { FileHelpers } from "tldraw";
import { asMessage, exhaustiveSwitchError } from "./utils.mjs";
class TldrawAiModule {
  constructor(opts = {}) {
    this.opts = opts;
  }
  dispose() {
  }
  /**
   * Creates and prepare a prompt, returning the prompt
   * and a function to handle changes.
   *
   * @param prompt The user's message or a configuration for the prompt
   */
  async generate(prompt) {
    const { transforms: _transformCtors = [] } = this.opts;
    const transforms = _transformCtors.map((ctor) => new ctor(this.opts.editor));
    const message = typeof prompt === "string" ? prompt : prompt.message;
    let _prompt = await this.getPrompt(message);
    for (const transform of transforms) {
      if (transform.transformPrompt) {
        _prompt = transform.transformPrompt(_prompt);
      }
    }
    transforms.reverse();
    const handleChange = (change) => {
      for (const transform of transforms) {
        if (transform.transformChange) {
          change = transform.transformChange(change);
        }
      }
      this.applyChange(change);
    };
    const handleChanges = (changes) => {
      for (const transform of transforms) {
        if (transform.transformChanges) {
          changes = transform.transformChanges(changes);
        }
      }
    };
    return {
      prompt: _prompt,
      handleChange,
      handleChanges
    };
  }
  /**
   * Apply a change to the editor.
   *
   * @param change The change to apply
   */
  applyChange(change) {
    const { editor } = this.opts;
    if (editor.isDisposed) return;
    try {
      switch (change.type) {
        case "createShape": {
          editor.createShape(change.shape);
          break;
        }
        case "updateShape": {
          editor.updateShape(change.shape);
          break;
        }
        case "deleteShape": {
          editor.deleteShape(change.shapeId);
          break;
        }
        case "createBinding": {
          editor.createBinding(change.binding);
          break;
        }
        case "updateBinding": {
          editor.updateBinding(change.binding);
          break;
        }
        case "deleteBinding": {
          editor.deleteBinding(change.bindingId);
          break;
        }
        default:
          exhaustiveSwitchError(change);
      }
    } catch (e) {
      console.error("Error handling change:", e);
    }
  }
  /**
   * Create the prompt to be sent to the AI.
   *
   * @param prompt The user's prompt
   * @param options Options to generate the input
   */
  async getPrompt(prompt, options = {}) {
    const { editor } = this.opts;
    const {
      contextBounds = editor.getViewportPageBounds(),
      promptBounds = editor.getViewportPageBounds()
    } = options;
    const content = options.canvasContent ?? this.getContent(promptBounds);
    const image = await this.getImage(content);
    return {
      message: asMessage(prompt),
      canvasContent: content,
      contextBounds: roundBox(contextBounds),
      promptBounds: roundBox(promptBounds),
      image
    };
  }
  /**
   * Get the content from the current page.
   *
   * @param bounds The bounds to get the content for
   */
  getContent(bounds) {
    const { editor } = this.opts;
    let content = {
      bindings: [],
      shapes: [],
      assets: [],
      ...editor.getContentFromCurrentPage(
        editor.getCurrentPageShapesSorted().filter((s) => bounds.includes(editor.getShapeMaskedPageBounds(s)))
      )
    };
    if (content) {
      content.shapes = structuredClone(content.shapes);
      content.bindings = structuredClone(content.bindings);
      delete content.schema;
      delete content.rootShapeIds;
    } else {
      content = {
        shapes: [],
        bindings: [],
        assets: []
      };
    }
    return content;
  }
  /**
   * Get a screenshot (data URL) of the prompt's content
   *
   * @param content The content to get the image from
   */
  async getImage(content) {
    if (!content.shapes.length) return void 0;
    const result = await this.opts.editor.toImage(content.shapes, {
      format: "jpeg",
      background: false,
      darkMode: false,
      padding: 0
      // will the context bounds take into account the padding?
    });
    return await FileHelpers.blobToDataUrl(result.blob);
  }
}
function roundBox(box) {
  const b = box.clone();
  b.x = Math.round(b.x);
  b.y = Math.round(b.y);
  b.width = Math.round(b.width);
  b.height = Math.round(b.height);
  return b;
}
export {
  TldrawAiModule
};
//# sourceMappingURL=TldrawAiModule.mjs.map
